/**
 * Modes modaux (gamme) : triades et 7èmes diatoniques par degré.
 * Utilisé par ChordSelectorModal, chordFormatter, Editor, Player.
 */

const DEGREES = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII']

// generic / major / minor (comportement actuel)
const TONAL_DEGREE_MAP = {
  I: { generic: 'I', major: 'I', minor: 'i' },
  II: { generic: 'II', major: 'ii', minor: 'ii°' },
  III: { generic: 'III', major: 'iii', minor: 'III' },
  IV: { generic: 'IV', major: 'IV', minor: 'iv' },
  V: { generic: 'V', major: 'V', minor: 'V' },
  VI: { generic: 'VI', major: 'vi', minor: 'VI' },
  VII: { generic: 'VII', major: 'vii°', minor: 'vii°' }
}

const TONAL_NATURE_MAP = {
  I: { major: 'Maj', minor: 'min' },
  II: { major: 'min', minor: 'dim' },
  III: { major: 'min', minor: 'Maj' },
  IV: { major: 'Maj', minor: 'min' },
  V: { major: 'Maj', minor: 'Maj' },
  VI: { major: 'min', minor: 'Maj' },
  VII: { major: 'dim', minor: 'dim' }
}

// 7èmes en majeur/mineur (pour getSeventhHint quand mode = major/minor)
const TONAL_SEVENTH_MAJOR = {
  I: 'M7', II: 'm7', III: 'm7', IV: 'M7', V: '7', VI: 'm7', VII: 'Ø'
}
const TONAL_SEVENTH_MINOR = {
  I: 'mM7', II: 'Ø', III: 'M7', IV: 'm7', V: '7', VI: 'M7', VII: 'o7'
}

// Modes modaux : pour chaque degré { label, nature, seventh }
const MODAL_SCALES = {
  ionian: {
    id: 'ionian',
    shortLabel: 'Ion',
    fullLabel: 'Ionien',
    degrees: {
      I: { label: 'I', nature: 'Maj', seventh: 'M7' },
      II: { label: 'ii', nature: 'min', seventh: 'm7' },
      III: { label: 'iii', nature: 'min', seventh: 'm7' },
      IV: { label: 'IV', nature: 'Maj', seventh: 'M7' },
      V: { label: 'V', nature: 'Maj', seventh: '7' },
      VI: { label: 'vi', nature: 'min', seventh: 'm7' },
      VII: { label: 'vii°', nature: 'dim', seventh: 'Ø' }
    }
  },
  dorian: {
    id: 'dorian',
    shortLabel: 'Dor',
    fullLabel: 'Dorien',
    degrees: {
      I: { label: 'i', nature: 'min', seventh: 'm7' },
      II: { label: 'ii', nature: 'min', seventh: 'm7' },
      III: { label: 'III', nature: 'Maj', seventh: 'M7' },
      IV: { label: 'IV', nature: 'Maj', seventh: 'M7' },
      V: { label: 'v', nature: 'min', seventh: 'm7' },
      VI: { label: 'vi°', nature: 'dim', seventh: 'Ø' },
      VII: { label: 'VII', nature: 'Maj', seventh: '7' }
    }
  },
  phrygian: {
    id: 'phrygian',
    shortLabel: 'Phr',
    fullLabel: 'Phrygien',
    degrees: {
      I: { label: 'i', nature: 'min', seventh: 'm7' },
      II: { label: 'II', nature: 'Maj', seventh: 'M7' },
      III: { label: 'III', nature: 'Maj', seventh: 'M7' },
      IV: { label: 'iv', nature: 'min', seventh: 'm7' },
      V: { label: 'v°', nature: 'dim', seventh: 'Ø' },
      VI: { label: 'VI', nature: 'Maj', seventh: 'M7' },
      VII: { label: 'vii', nature: 'min', seventh: 'm7' }
    }
  },
  lydian: {
    id: 'lydian',
    shortLabel: 'Lyd',
    fullLabel: 'Lydien',
    degrees: {
      I: { label: 'I', nature: 'Maj', seventh: 'M7' },
      II: { label: 'II', nature: 'Maj', seventh: 'M7' },
      III: { label: 'iii', nature: 'min', seventh: 'm7' },
      IV: { label: 'iv°', nature: 'dim', seventh: 'Ø' },
      V: { label: 'V', nature: 'Maj', seventh: 'M7' },
      VI: { label: 'vi', nature: 'min', seventh: 'm7' },
      VII: { label: 'vii', nature: 'min', seventh: 'm7' }
    }
  },
  mixolydian: {
    id: 'mixolydian',
    shortLabel: 'Mix',
    fullLabel: 'Mixolydien',
    degrees: {
      I: { label: 'I', nature: 'Maj', seventh: 'M7' },
      II: { label: 'ii', nature: 'min', seventh: 'm7' },
      III: { label: 'iii°', nature: 'dim', seventh: 'Ø' },
      IV: { label: 'IV', nature: 'Maj', seventh: 'M7' },
      V: { label: 'v', nature: 'min', seventh: 'm7' },
      VI: { label: 'vi', nature: 'min', seventh: 'm7' },
      VII: { label: 'VII', nature: 'Maj', seventh: '7' }
    }
  },
  aeolian: {
    id: 'aeolian',
    shortLabel: 'Aeo',
    fullLabel: 'Éolien',
    degrees: {
      I: { label: 'i', nature: 'min', seventh: 'm7' },
      II: { label: 'ii°', nature: 'dim', seventh: 'Ø' },
      III: { label: 'III', nature: 'Maj', seventh: 'M7' },
      IV: { label: 'iv', nature: 'min', seventh: 'm7' },
      V: { label: 'v', nature: 'min', seventh: 'm7' },
      VI: { label: 'VI', nature: 'Maj', seventh: 'M7' },
      VII: { label: 'VII', nature: 'Maj', seventh: '7' }
    }
  },
  locrian: {
    id: 'locrian',
    shortLabel: 'Loc',
    fullLabel: 'Locrien',
    degrees: {
      I: { label: 'i°', nature: 'dim', seventh: 'Ø' },
      II: { label: 'II', nature: 'Maj', seventh: '7' },
      III: { label: 'iii', nature: 'min', seventh: 'm7' },
      IV: { label: 'iv', nature: 'min', seventh: 'm7' },
      V: { label: 'V', nature: 'Maj', seventh: 'M7' },
      VI: { label: 'VI', nature: 'Maj', seventh: 'M7' },
      VII: { label: 'vii', nature: 'min', seventh: 'm7' }
    }
  }
}

const MODAL_IDS = Object.keys(MODAL_SCALES)

/** Ordre pour le cycle raccourci M (10 valeurs) */
export const DEGREE_MODE_CYCLE = ['generic', 'major', 'minor', ...MODAL_IDS]

/** Liste des modes modaux pour l'UI (Ion, Dor, …) */
export const MODAL_SCALE_OPTIONS = MODAL_IDS.map((id) => ({
  value: id,
  shortLabel: MODAL_SCALES[id].shortLabel,
  fullLabel: MODAL_SCALES[id].fullLabel
}))

/**
 * Label d'affichage d'un degré selon le mode (generic | major | minor | ionian | …).
 */
export function getDegreeLabelForMode(degree, degreeMode) {
  if (!degree || !DEGREES.includes(degree)) return degree
  if (degreeMode === 'generic' || degreeMode === 'major' || degreeMode === 'minor') {
    return TONAL_DEGREE_MAP[degree]?.[degreeMode] ?? degree
  }
  const scale = MODAL_SCALES[degreeMode]
  return scale?.degrees[degree]?.label ?? degree
}

/**
 * Nature de la triade (Maj, min, dim) pour le sous-label.
 */
export function getDegreeNatureForMode(degree, degreeMode) {
  if (!degree || !DEGREES.includes(degree)) return null
  if (degreeMode === 'generic') return null
  if (degreeMode === 'major' || degreeMode === 'minor') {
    return TONAL_NATURE_MAP[degree]?.[degreeMode] ?? null
  }
  const scale = MODAL_SCALES[degreeMode]
  return scale?.degrees[degree]?.nature ?? null
}

/**
 * Indice 7ème (M7, m7, 7, Ø, o7, mM7) pour les chiffrages 7, 65, 43, 2.
 * Retourne null si mode generic ou pas une 7ème.
 */
export function getSeventhHintForMode(degree, degreeMode) {
  if (!degree || !DEGREES.includes(degree)) return null
  if (degreeMode === 'generic') return null
  if (degreeMode === 'major') return TONAL_SEVENTH_MAJOR[degree] ?? null
  if (degreeMode === 'minor') return TONAL_SEVENTH_MINOR[degree] ?? null
  const scale = MODAL_SCALES[degreeMode]
  return scale?.degrees[degree]?.seventh ?? null
}

/**
 * Pour getFigureHint triades (5, 6, 64) : Maj → M, min → m, dim → o.
 */
export function getTriadHintForMode(degree, degreeMode) {
  const nature = getDegreeNatureForMode(degree, degreeMode)
  if (!nature) return null
  if (nature === 'Maj') return 'M'
  if (nature === 'min') return 'm'
  if (nature === 'dim') return 'o'
  return null
}

export { MODAL_SCALES, DEGREES }
